'use client';
import { useEffect, useMemo, useRef, useState } from 'react';

const delay = [0, 80, 160, 240, 320, 400];
interface TaskProgressAnimationProps {
  progress?: number;
  onFadeOutEnd?: () => void;
  isDark?: boolean;
}

export default function TaskProgressAnimation({
  progress = 0,
  onFadeOutEnd,
  isDark,
}: TaskProgressAnimationProps) {
  // 每行的点数（28列基准，波动6-8个点）
  const rowLengths = useMemo(() => [28, 24, 26, 22, 27], []);
  const maxLength = Math.max(...rowLengths);
  const totalDots = rowLengths.reduce((sum, n) => sum + n, 0);
  const FADE_DURATION = 1000; // ms
  const clampedProgress = Math.max(0, Math.min(100, progress));
  const filledCount = Math.round((totalDots * clampedProgress) / 100);

  const [fadeOutDelays, setFadeOutDelays] = useState<number[][]>([]);
  const [isPulsing, setIsPulsing] = useState(false);
  const pulseTimeoutRef = useRef<number | null>(null);

  // 计算全局索引起点，方便按顺序累计高亮
  const rowOffsets = useMemo(() => {
    const offsets: number[] = [];
    rowLengths.reduce((acc, len, idx) => {
      offsets[idx] = acc;
      return acc + len;
    }, 0);
    return offsets;
  }, [rowLengths]);

  // 初始化随机淡出延迟
  useEffect(() => {
    const delays = rowLengths.map(
      (length) => Array.from({ length }, () => Math.random() * 1200), // 0-1.2秒内随机
    );
    setFadeOutDelays(delays);
  }, [rowLengths]);

  useEffect(() => {
    // 1. 只要 progress 还在变，就关闭脉冲，并清除之前的定时器
    setIsPulsing(false);
    if (pulseTimeoutRef.current) {
      window.clearTimeout(pulseTimeoutRef.current);
    }

    // 2. If we haven't reached the end, start a timer
    if (clampedProgress < 100) {
      pulseTimeoutRef.current = window.setTimeout(() => {
        // If this executes, it means clampedProgress hasn't changed for 600ms
        setIsPulsing(true);
      }, 600);
    }

    return () => {
      if (pulseTimeoutRef.current) {
        window.clearTimeout(pulseTimeoutRef.current);
      }
    };
  }, [clampedProgress]); // 仅依赖 progress

  // 1. Improved Wave Logic: Use a smoother opacity range
  const getDotStyle = (rowIndex: number, colIndex: number) => {
    const rowLength = rowLengths[rowIndex];
    if (colIndex >= rowLength) return { opacity: 0, backgroundColor: 'transparent' };

    const globalIndex = rowOffsets[rowIndex] + colIndex;
    const isFilled = globalIndex < filledCount;

    if (isFilled) {
      const distanceFromHead = filledCount - 1 - globalIndex;
      const isInWaveRange = distanceFromHead < 6;

      // Calculate color based on proximity to the leading edge
      const headFade = Math.max(0, 1 - distanceFromHead / 6);
      const base = 170;
      const peak = isDark ? 255 : 0;
      const value = Math.round(base + (peak - base) * headFade);

      return {
        backgroundColor: `rgb(${value}, ${value}, ${value})`,
        opacity: 1,
        fadeDelay: fadeOutDelays[rowIndex]?.[colIndex] ?? 0,
        waveDelay: isInWaveRange ? delay[distanceFromHead] : 0,
        isInWaveRange,
      };
    }

    // Unfilled dots
    return {
      backgroundColor: '#6b7280',
      opacity: 0.25,
      isInWaveRange: false,
    };
  };

  // 2. The Render: Added CSS transition for smooth color/opacity shifts
  return (
    <div className="flex flex-col gap-[3px]">
      {rowLengths.map((length, rowIndex) => (
        <div key={rowIndex} className="flex gap-[3px]">
          {Array.from({ length: maxLength - length }).map((_, i) => (
            <div key={`empty-${i}`} className="w-[4px] h-[4px]" />
          ))}
          {Array.from({ length }).map((_, colIndex) => {
            const style = getDotStyle(rowIndex, colIndex);
            return (
              <div
                key={colIndex}
                className="size-[4px] rounded-full transition-all duration-300"
                style={{
                  backgroundColor: style.backgroundColor,
                  opacity: style.opacity,
                  animation:
                    clampedProgress >= 100
                      ? `fadeOut ${FADE_DURATION}ms ease-out ${style.fadeDelay}ms forwards`
                      : isPulsing && style.isInWaveRange
                        ? `wave 2000ms ease-in-out infinite ${style.waveDelay}ms`
                        : 'none',
                }}
              />
            );
          })}
        </div>
      ))}
      <style>{`
        @keyframes wave {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.85); }
        }
        @keyframes fadeOut {
          from { opacity: 1; transform: scale(1); }
          to { opacity: 0; transform: scale(0.5); }
        }
      `}</style>
    </div>
  );
}
